import { useEffect, useState } from 'react';

import { Container, Flex } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';

import { useAuth } from '@r-client/shared/feature/auth';
import { Spinner } from '@r-client/shared/ui/spinner';

import { Footer, SideBar } from '../../index';
import { ActionModalProvider } from '../action-modal-provider/action-modal-provider';
import { SKIP_LAYOUT_PATHS } from './constant';

interface IMainLayoutProps {
  children: React.ReactNode;
  appLastUpdated?: string;
  appReleaseVersion?: string;
  showFooter?: boolean;
}

export const MainLayout = observer(function MainLayout({
  children,
  appLastUpdated,
  appReleaseVersion,
  showFooter = false,
}: IMainLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const router = useRouter();
  const { isLoading, viewer } = useAuth();

  useEffect(() => {
    // If user do not has the session or unauthorized then pass him to login screen
    if (
      !SKIP_LAYOUT_PATHS.includes(router.pathname) &&
      !isLoading &&
      (!viewer || !viewer.isAuthenticated || !viewer.isAdmin)
    ) {
      router.push('/login');
    }
  }, [isLoading, router, viewer]);
  // Return public pages without layout and without auth check
  if (SKIP_LAYOUT_PATHS.includes(router.pathname)) {
    return <>{children}</>;
  }
  // Check user session for private pages
  if (isLoading || !viewer) return <Spinner fullscreen animatedLogo="line" />;

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return viewer?.isAdmin ? (
    <Flex direction="row" w="100%" h="100vh" minH="100%">
      <SideBar
        appLastUpdated={appLastUpdated}
        appReleaseVersion={appReleaseVersion}
        handleCollapse={handleCollapse}
        collapsed={collapsed}
      />
      <Flex
        as="main"
        h="full"
        flex="1"
        overflow="auto"
        direction="column"
        maxH={{
          base: showFooter ? 'calc(100% - 92px)' : 'full',
          md: showFooter ? 'calc(100% - 28px)' : 'full',
        }} // 28px = footer height and for mobile view header padding added
        mt={{ base: '64px', md: '0' }}
        backgroundColor="background.secondary"
      >
        <ActionModalProvider>
          <Container pt={0} px={8} pb={24} maxWidth="full">
            {children}
          </Container>
          {showFooter && <Footer />}
        </ActionModalProvider>
      </Flex>
    </Flex>
  ) : null;
});
